import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";
import moment from "moment";

import { useApp } from "../../../pages-vue/App/useApp";
import { MeetingCalendarService } from "../../../services/meeting-calendar-service";
import eventBus from "../../../utilities/eventBus";
import { jitsiRoomHandler } from "../../../utilities/jitsiRoomHandler";
import { ToastrService } from "../../../services/toastr.service";
import { ICallControls } from "../../vue-interfaces/messaging-service/i-notification";
import { RecurrenceTypeEnum } from "../../../enums/RecurrenceTypeEnum";
import { WeeklyMeetingDays } from "../../../enums/WeeklyMeetingDaysEnum";
import { occurenceText } from "../../../common/vue-helpers/modifiers/format-occurence-text";

import CancelMeetingEvent from "../CancelMeetingEvent/CancelMeetingEvent.vue";
import AddCalendarEvent from "../AddCalendarEvent/AddCalendarEvent.vue";
import ForwardMeetingEvent from "../ForwardMeetingEvent/ForwardMeetingEvent.vue";

export default defineComponent({
    inject: ["dialogRef"],

    data() {
        return {
            _meetingCalendarService: null as MeetingCalendarService,
            _toastrService: null as ToastrService,
            dialogReference: this.dialogRef as any,
            eventTitle: "" as string,
            description: "" as string,
            selectedEventData: null as any,
            isRecurring: false as boolean,
            eventDate: moment().format("MM/DD/YYY") as string,
            recurrenceStartDate: moment().format("MM/DD/YY") as string,
            recurrenceEndDate: moment().format("MM/DD/YY") as string,
            isOrganizer: false as boolean,
            organizerName: "" as string,
            recurrenceType: RecurrenceTypeEnum.None as any,
            daysOfWeek: "" as string,
            occurenceText
        };
    },

    created() {
        this._meetingCalendarService = Container.instance.get(MeetingCalendarService);
        this._toastrService = Container.instance.get(ToastrService);
    },

    mounted() {
        this.selectedEventData = this.dialogReference?.data?.selectedEvent;
        if (!this.selectedEventData) {
            return;
        }
        this.eventTitle = this.selectedEventData.title || "";
        this.description = this.selectedEventData?.extendedProps?.description || "";
        this.isRecurring = this.selectedEventData?.extendedProps?.isRecurring || false;
        this.isOrganizer = this.selectedEventData?.extendedProps?.isOrganizer || false;
        const recurringType = this.selectedEventData?.extendedProps?.recurringType;
        if (recurringType !== undefined && recurringType !== null) {
            this.recurrenceType = String(recurringType) as any;
            this.recurrenceStartDate = moment(this.selectedEventData?.extendedProps?.recurrenceStartDate).format(
                "MM/DD/YY"
            );
            this.recurrenceEndDate = moment(this.selectedEventData?.extendedProps?.recurrenceEndDate).format(
                "MM/DD/YY"
            );
        }

        const weeklyDaysOfWeekStr = this.selectedEventData?.extendedProps?.weeklyDaysOfWeekStr;
        if (weeklyDaysOfWeekStr.length) {
            const arr = weeklyDaysOfWeekStr.split(", ");
            const strArr = arr.map((day: string) => {
                return WeeklyMeetingDays.getValueUsingKey(day);
            });
            this.daysOfWeek = strArr.join(",");
        }
    },

    computed: {
        ...mapState(useApp, ["loggedInUserDetails"]),

        shouldShowJoinBtn() {
            if (this.selectedEventData) {
                const startTime = moment(this.selectedEventData.start);
                const endTime = moment(this.selectedEventData.end);
                if (this.selectedEventData.allDay) {
                    return startTime.isSame(moment(), "day");
                } else {
                    // Add 15 mins before start and 15 mins after end
                    const bufferedStart = moment(startTime).clone().subtract(15, "minutes");
                    const bufferedEnd = moment(endTime).clone().add(15, "minutes");
                    return moment().isBetween(bufferedStart, bufferedEnd, null, "[]");
                }
            }
            return false;
        },

        eventTime() {
            if (this.selectedEventData) {
                this.eventDate = moment(this.selectedEventData.start).format("MM/DD/YYYY");
                if (this.selectedEventData?.allDay) {
                    return "All Day";
                } else {
                    return `${moment(this.selectedEventData.start).format("hh:mm A")} - ${moment(this.selectedEventData.end).format("hh:mm A")}`;
                }
            }
            return "";
        },
        isPastEvent(): boolean {
            const event = this.selectedEventData;

            if (!event || !event.start) {
                return false;
            }

            const eventStart = moment(event.start);

            if (!eventStart.isValid()) {
                return false;
            }

            return eventStart.isSameOrBefore(moment());
        },
        getParticipantNames() {
            if (this.selectedEventData) {
                this.organizerName = this.selectedEventData.extendedProps?.organizerDetails;
                const names = this.selectedEventData.extendedProps?.participants.map((participant) => {
                    return `${participant.firstName} ${participant.lastName}`;
                });
                return names.join(", ");
            }
            return null;
        }
    },

    methods: {
        async joinMeeting() {
            let callControls: ICallControls = {
                audioEnabled: false,
                videoEnabled: false
            };

            try {
                const res = await this._meetingCalendarService.joinCalendarMeeting(this.selectedEventData?.id);
                await jitsiRoomHandler(this, res.roomId, this.selectedEventData.startTime, callControls, {
                    roomName: this.eventTitle
                });
                this.closeDialog();
            } catch (err) {
                console.error(err);
            }
        },

        async editMeeting(isEditSeries: boolean = false) {
            this.$dialog.open(AddCalendarEvent, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    mode: "edit",
                    selectedEvent: this.selectedEventData,
                    isEditSeries
                },
                onClose: async (response: any) => {
                    if (response.data === "success") {
                        this.closeDialog();
                        eventBus.emit("updateMeetingCalendar");
                        this._toastrService.success({
                            title: "Edited",
                            message: "Meeting edited successfully"
                        });
                    }
                }
            });
        },

        async forwardMeeting() {
            this.$dialog.open(ForwardMeetingEvent, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    conversationId: this.selectedEventData?.extendedProps?.conversationId,
                    shareChatHistory: false
                },
                onClose: async (response: any) => {
                    if (response.data === "success") {
                        this.closeDialog();
                        eventBus.emit("updateMeetingCalendar");
                        this._toastrService.success({
                            title: "Forwarded",
                            message: "Meeting forwarded successfully"
                        });
                    }
                }
            });
        },

        cancelMeeting(isCancelSeries: boolean = false) {
            this.$dialog.open(CancelMeetingEvent, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    isRecurringEvent: this.isRecurring,
                    isCancelSeries
                },
                onClose: async (options: any) => {
                    try {
                        if (options?.data?.status === "success") {
                            const payload = {
                                conversationId: this.selectedEventData?.extendedProps?.conversationId || "",
                                meetingId: this.selectedEventData?.id,
                                deleteRecurrence: options?.data?.cancelRecurrence,
                                reason: options.data?.reason
                            };

                            await this._meetingCalendarService.deleteMeeting(payload);
                            eventBus.emit("updateMeetingCalendar");
                            this.dialogReference.close("success");
                            this._toastrService.success({
                                title: "Cancelled",
                                message: "Meeting cancelled successfully"
                            });
                        }
                    } catch (error) {
                        console.error("Failed to cancel meeting", error);
                    }
                }
            });
        },

        closeDialog() {
            this.dialogReference.close();
        }
    }
});
